import * as signalR from "@microsoft/signalr";
import { markRaw, reactive } from "vue";
import { useUserStore } from "@/stores/UserStore";
import { NOTIFICATION_HUB_URL } from "./NotificationService";

// Sekme başına TEK bağlantı. Sunucu kullanıcı başına en çok 5 eşzamanlı bağlantıya izin verir,
// aşılırsa yeni bağlantı sessizce kapanır; bu yüzden bağlantı paylaşılır ve gizli sekmede bırakılır.
export const EVENTS = ["ReceiveNotification", "ReceiveAdminNotification", "AdminNotificationUpdated"];
const HIDDEN_STOP_MS = 60 * 1000;
const RETRY_MS = 10 * 1000;
const RELEASE_STOP_MS = 3 * 1000; // sayfa geçişinde kopup yeniden bağlanmayı önler

export const hubState = reactive({ status: "offline" }); // offline | connecting | connected | reconnecting

let connection = null; // markRaw: Vue Proxy'si "connection !== conn" kontrollerini bozar
let refs = 0;
let everConnected = false;
let retryTimer = null;
let hiddenTimer = null;
let releaseTimer = null;
let connectedToken = null;
let storeUnsub = null;
const handlers = new Map(EVENTS.map((e) => [e, new Set()]));
const resyncHandlers = new Set();

const currentToken = () => useUserStore().getToken;
const isVisible = () => typeof document === "undefined" || document.visibilityState !== "hidden";
const runResync = () => resyncHandlers.forEach((fn) => fn());

async function connect() {
  if (connection || refs <= 0 || !isVisible()) return;
  const token = currentToken();
  if (!token) return;
  connectedToken = token;
  hubState.status = "connecting";
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(NOTIFICATION_HUB_URL, {
      transport: signalR.HttpTransportType.WebSockets,
      skipNegotiation: true,
      accessTokenFactory: () => currentToken() || "",
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .build();
  EVENTS.forEach((name) => conn.on(name, (payload) => handlers.get(name).forEach((fn) => fn(payload))));
  conn.onreconnecting(() => {
    if (connection === conn) hubState.status = "reconnecting";
  });
  conn.onreconnected(() => {
    if (connection !== conn) return;
    hubState.status = "connected";
    runResync(); // kopukken gelen olaylar kaçırılır
  });
  // Token süresi dolunca sunucu bağlantıyı kapatır
  conn.onclose(() => {
    if (connection !== conn) return;
    connection = null;
    hubState.status = "offline";
    scheduleRetry();
  });
  connection = markRaw(conn);
  try {
    await conn.start();
    if (connection !== conn) return;
    hubState.status = "connected";
    if (everConnected) runResync();
    everConnected = true;
  } catch (e) {
    if (connection !== conn) return;
    connection = null;
    hubState.status = "offline";
    scheduleRetry();
  }
}

function scheduleRetry() {
  clearTimeout(retryTimer);
  if (refs <= 0) return;
  retryTimer = setTimeout(connect, RETRY_MS);
}

async function disconnect() {
  clearTimeout(retryTimer);
  const conn = connection;
  connection = null;
  hubState.status = "offline";
  if (conn) {
    try {
      await conn.stop();
    } catch (e) {
      /* yoksay */
    }
  }
}

function onVisibilityChange() {
  if (isVisible()) {
    clearTimeout(hiddenTimer);
    if (refs > 0 && !connection) {
      // Gizliyken kapatıldıysa dönüşte yeniden bağlan; eksik kalanlar için listeler yenilenir
      connect();
    }
  } else if (refs > 0) {
    clearTimeout(hiddenTimer);
    hiddenTimer = setTimeout(() => {
      if (!isVisible()) disconnect();
    }, HIDDEN_STOP_MS);
  }
}

// Çıkış yapılırsa bağlantıyı kapat, farklı kullanıcı girerse yeni token ile yeniden kur
function watchStore() {
  if (storeUnsub) return;
  storeUnsub = useUserStore().$subscribe(() => {
    const token = currentToken();
    if (refs <= 0) return;
    if (!token) {
      disconnect();
    } else if (connection && token !== connectedToken) {
      disconnect().then(connect);
    } else if (!connection) {
      connect();
    }
  });
}

// Bağlantıyı kullanmaya başlar; dönen fonksiyon bırakır (son kullanıcı bırakınca bağlantı kapanır)
export function acquireHub() {
  refs += 1;
  clearTimeout(releaseTimer);
  watchStore();
  if (refs === 1) document.addEventListener("visibilitychange", onVisibilityChange);
  connect();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    refs -= 1;
    if (refs > 0) return;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    clearTimeout(hiddenTimer);
    releaseTimer = setTimeout(() => {
      if (refs <= 0) disconnect();
    }, RELEASE_STOP_MS);
  };
}

export function subscribeHub(event, fn) {
  handlers.get(event).add(fn);
  return () => handlers.get(event).delete(fn);
}

// Yeniden bağlanınca listeleri/sayaçları tazelemek için
export function onHubResync(fn) {
  resyncHandlers.add(fn);
  return () => resyncHandlers.delete(fn);
}

export function stopHubNow() {
  refs = 0;
  clearTimeout(releaseTimer);
  clearTimeout(hiddenTimer);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  return disconnect();
}
